import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './Help.css';

// Help Center — ADDITIVE support hub. The existing /faq stays untouched and is linked from here.
// Search is a simple client-side filter over the articles on this page (no backend). SSR-safe: the
// filter state starts empty so the server renders the full list.
//
// LEGAL_REVIEW_REQUIRED: payment, fee, cancellation, refund and "Peace of Mind" copy below is marketing
// copy and must be reviewed by counsel before production.
// COMPLIANCE / CONFIRMED FACTS (Paul §10): cashless payments via Stripe; client service fee 12% + a
// one-time $10 booking fee; worker payout fee 10%; cancellation = FREE before a pro accepts, FREE more
// than 24h before, a ONE-HOUR fee under 24h before. Refund mechanics stay discreet: eligible issues are
// reviewed for a refund — never "money-back guarantee".

const TOPICS = [
  {
    id: 'booking',
    title: 'Booking & scheduling',
    icon: 'M8 2v4M16 2v4M3 10h18M5 6h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
    articles: [
      { q: 'How do I book a pro?', a: 'Pick the service you need, choose a time, and confirm. We match you with a vetted local pro — often in under 60 seconds. You can also browse available pros and pick one yourself.' },
      { q: 'How fast can a pro arrive?', a: 'For on-demand jobs we broadcast to nearby vetted pros and the first available one accepts. You can also schedule a job for a future date and time.' },
      { q: 'Can I choose a specific pro?', a: 'Yes. Browse available pros and their rates, then pick the one you want. If they’re unavailable we’ll help find an equivalent pro at the same rate.' },
      { q: 'How do I track my pro?', a: 'Once a pro accepts, you can track them in real time and chat in-app — no need to share your phone number.' },
    ],
  },
  {
    id: 'payments',
    title: 'Payments & fees',
    icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z',
    articles: [
      // COMPLIANCE: cashless via Stripe only.
      { q: 'How do payments work?', a: 'Everything is cashless and handled securely in the app, powered by Stripe. Your card is kept on file and you’re charged when the job is complete — no cash changes hands.' },
      // COMPLIANCE: 12% service fee + $10 one-time booking fee (to the platform).
      { q: 'What fees does BestHand charge?', a: 'You pay your pro’s rate plus a 12% service fee and a one-time $10 booking fee per booking. That’s it — no hidden charges. See the Pricing page for a full breakdown.' },
      { q: 'When am I charged?', a: 'Your card is on file when you book, but you’re charged once the work is complete. Any extra time or materials are itemized and shown before they’re charged.' },
      { q: 'Do you accept promo codes or credits?', a: 'Yes. Credits and promo codes apply automatically at checkout, and you can see how much you have left across your remaining uses.' },
    ],
  },
  {
    id: 'cancellations',
    title: 'Cancellations & refunds',
    icon: 'M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0ZM15 9l-6 6M9 9l6 6',
    articles: [
      // COMPLIANCE: cancellation policy confirmed by Paul — exact terms.
      { q: 'What is the cancellation policy?', a: 'It’s free to cancel before a pro accepts, and free to cancel more than 24 hours before the scheduled time. If you cancel less than 24 hours before the job, a one-hour fee (one hour at the service rate) applies, so the pro who set the time aside isn’t left empty-handed.' },
      { q: 'How do I cancel or reschedule?', a: 'Open your booking in the app to cancel or change the time. If you need a hand, contact our support team and we’ll help.' },
      // LEGAL_REVIEW_REQUIRED: refunds kept discreet — eligible issues reviewed, NOT "money-back guarantee".
      { q: 'Can I get a refund?', a: 'If something isn’t right with a job, our team works with you to make it right. A pro can redo the work, and eligible issues are reviewed for a refund on a case-by-case basis. See Trust & Safety for how our Peace of Mind program works.' },
      { q: 'What if my pro doesn’t show up?', a: 'You won’t be left stuck. Contact support and we’ll help rematch you with another available pro or sort out any charge.' },
    ],
  },
  {
    id: 'account',
    title: 'Account & app',
    icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21a8 8 0 0 1 16 0',
    articles: [
      { q: 'How do I create an account?', a: 'Download the BestHand app and sign up with your phone number or email. You can book a job in just a few taps.' },
      { q: 'How do I update my payment method?', a: 'Go to Payment Methods in the app to add, change or remove a card. Payments are securely processed by Stripe.' },
      { q: 'How do I get the app?', a: 'BestHand is available on the App Store and Google Play. Visit the Get the App page for download links.' },
      { q: 'How do I update my profile or address?', a: 'Open your profile in the app to update your name, contact details and saved addresses.' },
    ],
  },
  {
    id: 'safety',
    title: 'Safety',
    icon: 'M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6l-8-4ZM9 12l2 2 4-4',
    articles: [
      // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
      { q: 'Are pros vetted?', a: 'Yes. Every pro is background-checked before they can take jobs. Ratings are reviewed on an ongoing basis.' },
      { q: 'Is my payment protected?', a: 'Payments are cashless and processed securely in-app via Stripe. You’re charged when the job is done, and your card details are never shared with your pro.' },
      // LEGAL_REVIEW_REQUIRED: Peace of Mind framing — discreet, no insurance claim, no refund-anytime.
      { q: 'What if something goes wrong?', a: 'Your booking is protected by our Peace of Mind program. If something isn’t right, our team helps make it right — a pro can redo the work, and eligible issues are reviewed for a refund.' },
      { q: 'How do I report a concern?', a: 'Contact our support team right away through the app or the Contact page. We take every safety concern seriously.' },
    ],
  },
  {
    id: 'pros',
    title: 'For pros',
    icon: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.1-2.1 2.6-2.4Z',
    articles: [
      // COMPLIANCE: worker payout fee 10%.
      // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
      { q: 'How do I become a pro?', a: 'Sign up on the Become a Pro page, complete a background check and a quick verification step, and start accepting jobs on your own schedule.' },
      { q: 'How much does it cost a pro?', a: 'Our payout fee is 10% — among the lowest in the industry — so more of every dollar stays with you.' },
      { q: 'When do I get paid?', a: 'Earnings from completed jobs are paid out securely through the app. Check the Become a Pro page for details.' },
      { q: 'Do I need my own insurance?', a: 'Pros on BestHand are independent contractors and carry their own insurance. See the Independent Contractor page for details.' },
    ],
  },
];

// Flatten for FAQPage JSON-LD.
const ALL_ARTICLES = TOPICS.flatMap((t) => t.articles);
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: ALL_ARTICLES.map((a) => ({
    '@type': 'Question',
    name: a.q,
    acceptedAnswer: { '@type': 'Answer', text: a.a },
  })),
};

function matches(article, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return article.q.toLowerCase().includes(q) || article.a.toLowerCase().includes(q);
}

function Help() {
  const [query, setQuery] = React.useState('');

  const filteredTopics = TOPICS.map((t) => ({
    ...t,
    hits: t.articles.filter((a) => matches(a, query)),
  })).filter((t) => t.hits.length > 0);

  const hasResults = filteredTopics.length > 0;

  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      <Hero
        eyebrow="Help Center"
        title="How can we help?"
        subtitle="Find quick answers about bookings, payments, cancellations, your account and safety — or reach our support team any time."
      >
        <div />
      </Hero>

      {/* Search */}
      <section className={s.searchSec}>
        <div className={s.searchWrap}>
          <div className={s.searchBox}>
            <svg viewBox="0 0 24 24" aria-hidden="true" className={s.searchIcon}>
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="m20 20-3.5-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              className={s.searchInput}
              placeholder="Search help articles…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search help articles"
            />
          </div>
        </div>
      </section>

      {/* Topic tiles (jump nav) */}
      {!query && (
        <section className={s.tilesSec}>
          <div className={s.wrap}>
            <div className={s.tiles}>
              {TOPICS.map((t) => (
                <a key={t.id} href={`#${t.id}`} className={s.tile}>
                  <span className={s.tileIc}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d={t.icon} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className={s.tileName}>{t.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Articles by theme */}
      <section className={s.articlesSec}>
        <div className={s.wrap}>
          {!hasResults && (
            <p className={s.noResults}>
              No articles match “{query}”. Try a different word, browse the topics above, or{' '}
              <Link to="/contact-us" className={s.inlineLink}>contact support</Link>.
            </p>
          )}
          {filteredTopics.map((t) => (
            <div className={s.group} id={t.id} key={t.id}>
              <h2 className={s.groupTitle}>
                <span className={s.groupIc}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d={t.icon} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {t.title}
              </h2>
              <div className={s.qaList}>
                {t.hits.map((a) => (
                  <details className={s.qa} key={a.q}>
                    <summary className={s.qaQ}>{a.q}</summary>
                    <p className={s.qaA}>{a.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ))}

          {/* Related links */}
          <div className={s.related}>
            <span>More resources:</span>
            <Link to="/pricing" className={s.relLink}>Pricing</Link>
            <Link to="/trust-safety" className={s.relLink}>Trust &amp; Safety</Link>
            <Link to="/how-it-works" className={s.relLink}>How it works</Link>
            <Link to="/faq" className={s.relLink}>Full FAQ</Link>
          </div>
        </div>
      </section>

      {/* Still need help */}
      <section className={`${s.contactSec}`}>
        <div className={s.wrap}>
          <div className={s.contactCard}>
            <h2 className={s.contactTitle}>Still need help?</h2>
            <p className={s.contactText}>Our support team is here for you. Reach out and we’ll get you sorted.</p>
            <div className={s.contactActions}>
              <Link to="/contact-us" className={s.contactBtn}>Contact support</Link>
              <a href="mailto:support@besthandapp.com" className={s.contactBtnAlt}>support@besthandapp.com</a>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Ready to get the job done?"
        subtitle="Book a vetted local pro with upfront, industry-low pricing."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="Browse all services"
        secondaryTo="/services"
      />
    </main>
  );
}

export default withStyles(s)(Help);
